import { makeAutoObservable } from 'mobx';
import { url } from '@monorepo/tools/src/lib/types/url';
import { CampaignGroupModel } from './campaign-group.model';
import { AdvertiserModel } from './advertiser.model';
import { IStrategy, StrategyModel } from './strategy.model';
import { ITargetingForm, TargetingModel } from './targeting.model';
import { DailyBudgetModel, IDailyBudget } from './daily-budget.model';
import { IRules, RulesModel } from './rules.model';
import { capitalCase } from 'change-case';
import { CreativeSelectionMode } from '../enums/creative-selection-modes';
import { CampaignType } from '../enums/campaign-types';
import { DealType, DealTypes } from '../enums/deal-types';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { DealModel } from './deal.model';
import { CreativeModel, ICreative } from './creative.model';
import {
	buildCampaignDestUrl,
	buildCampaignDestinationDtoFromDestination,
	resolveDestinationInternalPricing,
	resolveDestinationStateFromUrl,
} from '../utils/campaign-destination';
import { formatWebappsCidLabel } from '../utils/webapps-cid-options';
import { Status, Statuses } from '../enums/status';
import { WhiteBlackLists } from '../enums/white-black-list-enum';
import { AccountsModel, IAccount, resolveUseInternalPricing } from './account.model';
import { DestinationModel } from './destination.model';
import { isDestinationLinkActive } from '../utils/campaign-destination-weights';
import { IDomainFactorCreateForm } from './domain-factor.model';

export const ALL_PHASE = {
	id: 1,
	name: 'ALL',
};

export type IEntity = {
	id?: id;
	name: string;
};

export type BannerSize = {
	id?: id;
	width?: number;
	height?: number;
};

export interface ICampaignSatellitesDetails {
	black_sites_counter: number;
	white_sites_counter: number;
	bid_keys_counter: number;
	trash_counter: number;
	models_params: number;
}

export interface ICampaignDeepCloneOptions {
	source_campaign_id?: id;
	clone_sites_in_target: boolean;
	clone_bid_keys: boolean;
	clone_trash_keys: boolean;
	clone_models_params: boolean;
	clone_cache_key_stats: boolean;
	clone_design_score: boolean;
	clone_domain_factors: boolean;
}

export const DEFAULT_CACHE_KEY_GENERATOR = 'TagKeyGenerator';

export interface IWebappsCidDto {
	id?: id;
	cid?: id | number;
	product_id?: id | number;
	product_name?: string;
}

export interface ICampaignDestinationDto {
	id?: id;
	name?: string;
	dest_url?: url;
	yid?: string;
	account?: IAccount;
	advertiser?: { id?: id; name?: string; user_cap_enabled?: boolean };
	creatives?: ICampaignCreativeLink[];
}

export interface ICampaignDestination {
	id?: id;
	destination_dto?: ICampaignDestinationDto;
	webapps_cid_dto?: IWebappsCidDto;
	additional_url_params?: string;
	additionalUrlParams?: string;
	status?: Status;
	weight?: number;
	daily_max_sales?: number | null;
	daily_max_loss?: number | null;
	creatives?: CreativeModel[];
}

export interface ICampaignDestinationForm {
	id?: id;
	destination_dto: ICampaignDestinationDto;
	webapps_cid_dto?: IWebappsCidDto;
	additional_url_params?: string;
	additionalUrlParams?: string;
	status?: Status;
	weight?: number;
	daily_max_sales?: number | null;
	daily_max_loss?: number | null;
	creatives?: CreativeModel[];
}

export interface ICampaignCreativeLink {
	id?: id;
	status?: Status;
	weight?: number;
	creative: ICreative;
}

export interface ICampaignPhase {
	id?: number;
	phase: IEntity;
	strategy?: IStrategy;
	daily_budget?: IDailyBudget;
	rules?: IRules;
	active?: boolean;
	cache_key_generator?: string;
}

/**
 * The campaign the campaign crud page uses
 */
export interface ICampaign {
	id?: id;
	yid?: string;
	name?: string;
	advertiser?: AdvertiserModel;
	account?: AccountsModel;
	campaign_group?: CampaignGroupModel;
	status?: Status;
	parent?: CampaignModel;
	priority?: number;
	secondary_priority?: number;
	cache_key_generator?: string;
	campaign_type?: CampaignType;
	deal_type?: DealType;
	auction_type?: string;
	deals?: DealModel[];
	billing_ids?: id[];
	strategy?: StrategyModel;
	dest_url?: url;
	campaign_destination?: ICampaignDestination;
	campaign_destinations?: ICampaignDestination[];
	targeting?: TargetingModel;
	blocked_hours?: number[];
	daily_budget?: DailyBudgetModel;
	default_payout?: number;
	campaign_phases?: ICampaignPhase[];
	black_listed_sites?: Set<string>;
	white_listed_sites?: Set<string>;
	black_listed_keys?: Set<string>;
	white_listed_keys?: Set<string>;
	creatives?: ICampaignCreativeLink[] | CreativeModel[];
	creative_selection_mode?: CreativeSelectionMode;
	black_listed_sizes?: BannerSize[];
	white_listed_sizes?: BannerSize[];
	allow_multiple_sales?: boolean;
	campaign_satellites_details?: ICampaignSatellitesDetails;
	deep_clone_campaign?: ICampaignDeepCloneOptions;
	design_score_picker?: string;
	format_score_picker?: string;
	auctionType?: string;
}

/**
 * The campaign the campaign edit page uses
 */
export interface ICampaignEdit {
	id?: id;
	name?: string;
	advertiser?: AdvertiserModel;
	account?: IAccount;
	campaign_group?: CampaignGroupModel;
	status?: Status;
	parent?: CampaignModel;
	priority?: number;
	secondary_priority?: number;
	cache_key_generator?: string;
	campaign_type?: CampaignType;
	deal_type?: DealType;
	deals?: DealModel[];
	billing_ids?: id[];
	strategy?: IStrategy;
	dest_url?: url;
	targeting?: TargetingModel;
	blocked_hours?: number[];
	daily_budget?: DailyBudgetModel;
	default_payout?: number;
	rules?: RulesModel;
	creative_selection_mode?: CreativeSelectionMode;
	black_listed_sizes?: BannerSize[];
	white_listed_sizes?: BannerSize[];
	daily_domain_max_loss?: number | null;
	design_score_picker?: string;
	format_score_picker?: string;
	allow_multiple_sales?: boolean;
}

/**
 * The campaign to send to server
 */
export interface ICampaignCreateForm {
	id?: id;
	yid?: string;
	name?: string;
	advertiser?: { id?: id }; // Advertiser id
	campaign_group?: { id?: id } | null; // Campaign group id
	account?: IAccount;
	status?: Status;
	parent?: { id?: id };
	priority?: number;
	secondary_priority?: number;
	campaign_type?: CampaignType;
	auction_type?: string;
	deals?: DealModel[];
	billing_ids?: id[];
	strategy?: { id: id } | null;
	campaign_destinations?: ICampaignDestinationForm[];
	targeting?: ITargetingForm;
	blocked_hours?: number[];
	daily_budget?: IDailyBudget;
	campaign_phases?: ICampaignPhase[];
	creatives?: number[];
	creative_selection_mode?: CreativeSelectionMode;
	black_listed_sizes?: BannerSize[];
	white_listed_sizes?: BannerSize[];
	daily_domain_max_loss?: number | null;
	deep_clone_campaign?: ICampaignDeepCloneOptions;
	design_score_picker?: string;
	format_score_picker?: string;
	allow_multiple_sales?: boolean;
	domain_factors?: IDomainFactorCreateForm[];
}

export interface ICampaignEditForm {
	id?: id;
	yid?: string;
	name?: string;
	advertiser?: { id?: id }; // Advertiser id
	account?: IAccount;
	campaign_group?: { id?: id } | null; // Campaign group id
	status?: Status;
	parent?: { id?: id };
	priority?: number;
	secondary_priority?: number;
	campaign_type?: CampaignType;
	auction_type?: string;
	deals?: DealModel[];
	billing_ids?: id[];
	strategy?: { id?: id } | null;
	campaign_destinations?: ICampaignDestinationForm[];
	targeting?: ITargetingForm;
	blocked_hours?: number[];
	daily_budget?: IDailyBudget;
	campaign_phases?: ICampaignPhase[];
	creatives?: number[];
	creative_selection_mode?: CreativeSelectionMode;
	black_listed_sizes?: BannerSize[];
	white_listed_sizes?: BannerSize[];
	daily_domain_max_loss?: number | null;
	design_score_picker?: string;
	format_score_picker?: string;
	allow_multiple_sales?: boolean;
}

export interface ICampaignTargetingListing {
	black_listed_sites?: string[];
	white_listed_sites?: string[];
	black_listed_keys?: string[];
	white_listed_keys?: string[];
}

export class CampaignPhaseModel implements ICampaignPhase {
	id?: number;
	phase: IEntity;
	strategy?: StrategyModel;
	daily_budget?: DailyBudgetModel;
	rules?: RulesModel;
	active?: boolean;
	cache_key_generator?: string;

	constructor(data: ICampaignPhase) {
		this.id = data.id || 0;
		this.phase = data.phase;
		this.strategy = data.strategy ? new StrategyModel(data.strategy) : undefined;
		this.daily_budget = new DailyBudgetModel(data.daily_budget);
		this.rules = new RulesModel(data.rules);
		this.active = Boolean(data.active);
		this.cache_key_generator = data.cache_key_generator;

		makeAutoObservable(this);
	}

	getStrategy(): StrategyModel | undefined {
		return this.strategy;
	}

	setStrategy(strategy: StrategyModel | undefined): void {
		this.strategy = strategy;
	}

	setRules(rules: RulesModel): void {
		this.rules = rules;
	}

	setBudget(budget: DailyBudgetModel): void {
		this.daily_budget = budget;
	}

	setActive(active: boolean): void {
		this.active = active;
	}

	public getCacheKeyGenerator(): string | undefined {
		return this.cache_key_generator;
	}

	public setCacheKeyGenerator(cache_key_generator: string): void {
		this.cache_key_generator = cache_key_generator;
	}
}

export class CampaignDestinationLinkModel implements ICampaignDestination {
	id?: id;
	destination_dto?: ICampaignDestinationDto;
	webapps_cid_dto?: IWebappsCidDto;
	additional_url_params?: string;
	status?: Status;
	weight?: number;
	daily_max_sales?: number | null;
	daily_max_loss?: number | null;
	dest_url?: url;
	selectedDestinationId?: id;
	selectedDestinationTemplateUrl?: url;
	selectedWebappsCid?: id | string;
	selectedWebappsCidDto?: IWebappsCidDto;
	selectedDestinationDto?: ICampaignDestinationDto;
	destinationUseInternalPricing?: boolean;
	hydratedDestinationName?: string;
	hydratedWebappsCidLabel?: string;
	creatives?: CreativeModel[] | undefined;

	constructor(data?: ICampaignDestination, fallbackFinalUrl?: string) {
		this.id = data?.id;
		this.status = data?.status ?? Statuses.Active;
		this.weight = data?.weight ?? 1;
		this.daily_max_sales = data?.daily_max_sales ?? null;
		this.daily_max_loss = data?.daily_max_loss ?? null;
		this.destination_dto = data?.destination_dto;
		this.webapps_cid_dto = data?.webapps_cid_dto;
		this.additional_url_params = data?.additional_url_params ?? data?.additionalUrlParams ?? '';
		this.creatives = data?.creatives ?? [];

		if (data?.destination_dto) {
			this.applyFromCampaignDestination(data, fallbackFinalUrl);
		}

		makeAutoObservable(this);
	}

	public getId(): id | undefined {
		return this.id;
	}

	public getCatalogDestinationId(): id | undefined {
		return this.selectedDestinationId ?? this.destination_dto?.id;
	}

	public getName(): string {
		return this.hydratedDestinationName || this.destination_dto?.name || this.selectedDestinationDto?.name || 'Destination';
	}

	public isActive(): boolean {
		return isDestinationLinkActive(this);
	}

	public setStatus(status: Status): void {
		this.status = status;
	}

	public setWeight(weight: number): void {
		this.weight = weight < 0 || Number.isNaN(weight) ? 0 : weight;
	}

	public getDailyMaxSales(): number | null | undefined {
		return this.daily_max_sales;
	}

	public setDailyMaxSales(daily_max_sales: number | null): void {
		this.daily_max_sales = daily_max_sales != null && (daily_max_sales < 0 || Number.isNaN(daily_max_sales)) ? 0 : daily_max_sales;
	}

	public getDailyMaxLoss(): number | null | undefined {
		return this.daily_max_loss;
	}

	public setDailyMaxLoss(daily_max_loss: number | null): void {
		this.daily_max_loss = daily_max_loss != null && (daily_max_loss < 0 || Number.isNaN(daily_max_loss)) ? 0 : daily_max_loss;
	}

	public getDestinationUrl(): url | undefined {
		return this.dest_url;
	}

	public getSelectedDestinationId(): id | undefined {
		return this.selectedDestinationId;
	}

	public getSelectedWebappsCid(): id | string | undefined {
		return this.selectedWebappsCid;
	}

	public getSelectedWebappsCidDto(): IWebappsCidDto | undefined {
		return this.selectedWebappsCidDto;
	}

	public getSelectedDestinationDto(): ICampaignDestinationDto | undefined {
		return this.selectedDestinationDto;
	}

	public getAdditionalUrlParams(): string {
		return this.additional_url_params || '';
	}

	public setAdditionalUrlParams(additionalUrlParams: string): void {
		this.additional_url_params = additionalUrlParams;
	}

	public getDestinationUseInternalPricing(): boolean {
		return this.destinationUseInternalPricing === true;
	}

	public setDestinationUseInternalPricing(useInternalPricing: boolean | undefined): void {
		this.destinationUseInternalPricing = useInternalPricing;
	}

	public setSelectedWebappsCid(selectedWebappsCid: id | string | undefined): void {
		this.selectedWebappsCid = selectedWebappsCid;
	}

	public setSelectedWebappsCidDto(dto: IWebappsCidDto | undefined): void {
		this.selectedWebappsCidDto = dto;
		this.webapps_cid_dto = dto;
	}

	public setSelectedDestinationDto(dto: ICampaignDestinationDto | undefined): void {
		this.selectedDestinationDto = dto;
		this.destination_dto = dto;
	}
	public setCreatives(creatives: CreativeModel[]): void {
		this.creatives = creatives;
	}
	public getCreatives(): CreativeModel[] | undefined {
		return this.creatives;
	}

	public applyFromDestination(
		destination: DestinationModel,
		advertiserFallback?: AdvertiserModel,
		accounts?: { getId(): id | undefined; getUseInternalPricing(): boolean | undefined }[]
	): void {
		const dto = buildCampaignDestinationDtoFromDestination(destination, advertiserFallback);
		const useInternalPricing = resolveDestinationInternalPricing(destination, accounts);

		this.selectedDestinationId = destination.getId();
		this.selectedDestinationTemplateUrl = destination.getDestUrl();
		this.destinationUseInternalPricing = useInternalPricing;
		this.setSelectedDestinationDto(dto);
		this.hydratedDestinationName = destination.getName();
		this.selectedWebappsCid = undefined;
		this.selectedWebappsCidDto = undefined;
		this.webapps_cid_dto = undefined;
		this.hydratedWebappsCidLabel = undefined;
		this.additional_url_params = '';
		this.recomputeDestinationUrl();
	}

	public applyFromCampaignDestination(campaignDestination: ICampaignDestination, fallbackFinalUrl?: string): void {
		const destinationDto = campaignDestination.destination_dto;
		if (!destinationDto?.id || !destinationDto.dest_url) {
			return;
		}

		this.id = campaignDestination.id;
		this.status = campaignDestination.status ?? this.status ?? Statuses.Active;
		this.weight = campaignDestination.weight ?? this.weight ?? 0;
		this.daily_max_sales = campaignDestination.daily_max_sales ?? this.daily_max_sales ?? null;
		this.daily_max_loss = campaignDestination.daily_max_loss ?? this.daily_max_loss ?? null;
		this.setSelectedDestinationDto({ ...destinationDto });
		const useInternalPricing = resolveUseInternalPricing(destinationDto.account) === true;
		const finalUrl = fallbackFinalUrl || destinationDto.dest_url;
		this.hydratedDestinationName = destinationDto.name;

		this.applyFromUrlMatch({
			destinationId: destinationDto.id,
			templateUrl: destinationDto.dest_url,
			useInternalPricing,
			finalUrl,
		});

		const webappsCid = campaignDestination.webapps_cid_dto?.cid;
		if (webappsCid != null && webappsCid !== '') {
			this.setSelectedWebappsCid(webappsCid);
			this.setSelectedWebappsCidDto({ ...campaignDestination.webapps_cid_dto });
			this.hydratedWebappsCidLabel = formatWebappsCidLabel(webappsCid, campaignDestination.webapps_cid_dto?.product_name);
		}

		const additionalFromApi = campaignDestination.additional_url_params ?? campaignDestination.additionalUrlParams;
		if (additionalFromApi != null) {
			this.setAdditionalUrlParams(additionalFromApi);
		}

		this.recomputeDestinationUrl();
	}

	public applyFromUrlMatch(params: { destinationId: id; templateUrl: url; useInternalPricing: boolean; finalUrl: string }): void {
		const { webappsCid, additionalUrlParams } = resolveDestinationStateFromUrl({
			template: params.templateUrl,
			finalUrl: params.finalUrl,
			useInternalPricing: params.useInternalPricing,
		});

		this.selectedDestinationId = params.destinationId;
		this.selectedDestinationTemplateUrl = params.templateUrl;
		this.destinationUseInternalPricing = params.useInternalPricing;
		this.setSelectedWebappsCid(webappsCid);
		this.setAdditionalUrlParams(additionalUrlParams);
		this.recomputeDestinationUrl();
	}

	public getFormData(): ICampaignDestinationForm | undefined {
		if (!this.selectedDestinationId || !this.selectedDestinationDto) {
			return undefined;
		}

		const body: ICampaignDestinationForm = {
			destination_dto: { ...this.selectedDestinationDto, id: this.selectedDestinationId },
			status: this.status ?? Statuses.Active,
			weight: this.weight ?? 0,
			daily_max_sales: this.daily_max_sales ?? null,
			daily_max_loss: this.daily_max_loss ?? null,
		};

		if (this.id) {
			body.id = this.id;
		}

		if (this.destinationUseInternalPricing === true && this.selectedWebappsCidDto) {
			body.webapps_cid_dto = { ...this.selectedWebappsCidDto };
		}

		body.additional_url_params = this.additional_url_params?.trim() ?? '';

		return body;
	}

	public recomputeDestinationUrl(): void {
		const template = this.selectedDestinationTemplateUrl;
		if (!template) {
			this.dest_url = '';
			return;
		}
		this.dest_url = buildCampaignDestUrl({
			template,
			cid: this.selectedWebappsCid,
			additionalParams: this.additional_url_params,
			useInternalPricing: this.destinationUseInternalPricing,
		});
	}
}

export class CampaignModel implements ICampaign {
	id?: id;
	yid?: string;
	name?: string;
	advertiser?: AdvertiserModel;
	campaign_group?: CampaignGroupModel;
	account?: AccountsModel;
	status?: Status;
	parent?: CampaignModel;
	priority?: number;
	secondary_priority?: number;
	cache_key_generator?: string;
	campaign_type?: CampaignType;
	deal_type?: DealType;
	auction_type?: string;
	deals?: DealModel[];
	billing_ids?: id[];
	strategy?: StrategyModel;
	dest_url?: url;
	targeting?: TargetingModel;
	blocked_hours?: number[];
	daily_budget?: DailyBudgetModel;
	default_payout?: number;
	campaign_phases?: CampaignPhaseModel[];
	rules?: RulesModel;
	black_listed_sites?: Set<string>;
	white_listed_sites?: Set<string>;
	black_listed_keys?: Set<string>;
	white_listed_keys?: Set<string>;
	creatives?: CreativeModel[];
	black_listed_sizes?: BannerSize[];
	white_listed_sizes?: BannerSize[];
	banner_list_type?: WhiteBlackLists;
	creative_selection_mode?: CreativeSelectionMode;
	daily_domain_max_loss?: number | null;
	allow_multiple_sales?: boolean;
	campaign_satellites_details?: ICampaignSatellitesDetails;
	deep_clone_campaign?: ICampaignDeepCloneOptions;
	design_score_picker?: string;
	format_score_picker?: string;
	campaign_destination?: ICampaignDestination;
	campaign_destinations?: CampaignDestinationLinkModel[];

	constructor(campaign?: ICampaign) {
		this.id = campaign?.id;
		this.name = campaign?.name;
		this.advertiser = campaign?.advertiser ? new AdvertiserModel(campaign.advertiser) : undefined;
		const firstDestinationDto =
			campaign?.campaign_destinations?.[0]?.destination_dto ?? campaign?.campaign_destination?.destination_dto;
		if (!this.advertiser && firstDestinationDto?.advertiser) {
			this.advertiser = new AdvertiserModel(firstDestinationDto.advertiser);
		}
		this.account = campaign?.account ? new AccountsModel(campaign.account) : undefined;
		if (!this.account && firstDestinationDto?.account) {
			this.account = new AccountsModel(firstDestinationDto.account);
		}
		this.campaign_group = campaign?.campaign_group ? new CampaignGroupModel(campaign.campaign_group) : undefined;
		this.status = campaign?.status;
		this.parent = campaign?.parent ? new CampaignModel(campaign?.parent) : undefined;
		this.priority = campaign?.priority;
		this.secondary_priority = campaign?.secondary_priority;
		this.cache_key_generator = campaign?.cache_key_generator;

		this.campaign_type = campaign?.campaign_type;
		this.deal_type = campaign?.deal_type ? (capitalCase(campaign?.deal_type) as DealType) : DealTypes.OpenAuction;
		this.auction_type = campaign?.auction_type ? (capitalCase(campaign?.auction_type) as string) : DealTypes.OpenAuction;
		this.deals = campaign?.deals?.map(deal => new DealModel(deal));

		this.billing_ids = campaign?.billing_ids;
		this.strategy = campaign?.strategy ? new StrategyModel(campaign.strategy) : undefined;
		this.dest_url = campaign?.dest_url;
		this.campaign_destination = campaign?.campaign_destination;
		const destinationSources =
			campaign?.campaign_destinations && campaign.campaign_destinations.length > 0
				? campaign.campaign_destinations
				: campaign?.campaign_destination
					? [campaign.campaign_destination]
					: [];
		this.campaign_destinations = destinationSources.map(
			(destination, index) => new CampaignDestinationLinkModel(destination, index === 0 ? campaign?.dest_url : undefined)
		);
		this.syncPrimaryDestinationUrl();
		this.targeting = campaign?.targeting ? new TargetingModel(campaign.targeting) : undefined;
		this.blocked_hours = campaign?.blocked_hours || [];
		this.daily_budget = campaign?.daily_budget ? new DailyBudgetModel(campaign.daily_budget) : undefined;
		this.default_payout = campaign?.default_payout;
		this.campaign_phases = campaign?.campaign_phases?.map(phase => new CampaignPhaseModel(phase));
		if (this.cache_key_generator && this.campaign_phases?.length) {
			const rootGenerator = this.cache_key_generator;
			this.campaign_phases.forEach(p => {
				if (!p.getCacheKeyGenerator()) {
					p.setCacheKeyGenerator(rootGenerator);
				}
			});
		}
		this.black_listed_sites = new Set<string>(campaign?.black_listed_sites ?? []);
		this.white_listed_sites = new Set<string>(campaign?.white_listed_sites ?? []);
		this.black_listed_keys = new Set<string>(campaign?.black_listed_keys ?? []);
		this.white_listed_keys = new Set<string>(campaign?.white_listed_keys ?? []);
		this.black_listed_sizes = campaign?.black_listed_sizes || [];
		this.white_listed_sizes = campaign?.white_listed_sizes || [];
		this.banner_list_type = (campaign?.white_listed_sizes || [])?.length > 0 ? WhiteBlackLists.Whitelist : WhiteBlackLists.Blacklist;
		this.creatives = campaign?.creatives?.map(campaignCreative => {
			if ('creative' in campaignCreative && campaignCreative.creative) {
				return new CreativeModel((campaignCreative as ICampaignCreativeLink).creative);
			}
			return new CreativeModel(campaignCreative as ICreative);
		});
		this.creative_selection_mode = campaign?.creative_selection_mode;
		this.yid = campaign?.yid;
		this.campaign_satellites_details = campaign?.campaign_satellites_details ?? {
			black_sites_counter: 0,
			white_sites_counter: 0,
			bid_keys_counter: 0,
			trash_counter: 0,
			models_params: 0,
		};
		this.design_score_picker = campaign?.design_score_picker;
		this.format_score_picker = campaign?.format_score_picker;
		this.allow_multiple_sales = campaign?.allow_multiple_sales ?? true;

		// initialize only for duplicate flow
		if (campaign?.id || campaign?.deep_clone_campaign) {
			this.deep_clone_campaign = campaign?.deep_clone_campaign ?? {
				source_campaign_id: campaign?.id,
				clone_sites_in_target: false,
				clone_bid_keys: false,
				clone_trash_keys: false,
				clone_models_params: false,
				clone_cache_key_stats: false,
				clone_design_score: false,
				clone_domain_factors: false,
			};
		}

		makeAutoObservable(this);
	}

	public getId(): id | undefined {
		return this.id;
	}

	public setId(id: id) {
		this.id = id;
	}

	public getName() {
		return this.name;
	}

	public setName(name: string) {
		this.name = name;
	}

	public getAdvertiser(): AdvertiserModel | undefined {
		return this.advertiser;
	}

	public getAccount(): AccountsModel | undefined {
		return this.account;
	}

	public setAccount(account: AccountsModel): void {
		this.account = account;
	}

	public setAdvertiser(advertiser: AdvertiserModel): void {
		this.advertiser = advertiser;
	}

	public getCampaignGroup(): CampaignGroupModel | undefined {
		return this.campaign_group;
	}

	public setCampaignGroup(campaignGroup: CampaignGroupModel | undefined): void {
		this.campaign_group = campaignGroup;
	}

	public getStatus(): Status | undefined {
		return this.status;
	}

	public setParentCampaign(parent: CampaignModel | undefined): void {
		this.parent = parent;
	}

	public getParentCampaign(): CampaignModel | undefined {
		return this.parent;
	}

	public setStatus(status: Status): void {
		this.status = status;
	}

	public getPriority(): number | undefined {
		return this.priority;
	}

	public setPriority(priority: number): void {
		this.priority = priority;
	}

	public getSecondaryPriority(): number | undefined {
		return this.secondary_priority;
	}

	public setSecondaryPriority(secondary_priority: number): void {
		this.secondary_priority = secondary_priority;
	}

	public getCacheKeyGenerator(): string | undefined {
		return this.cache_key_generator;
	}

	public setCacheKeyGenerator(cache_key_generator: string): void {
		this.cache_key_generator = cache_key_generator;
	}

	public getCampaignType(): CampaignType | undefined {
		return this.campaign_type;
	}

	public setCampaignType(campaign_type: CampaignType): void {
		this.campaign_type = campaign_type;
	}

	public getDealType(): DealType | undefined {
		return this.deal_type;
	}

	public setDealType(deal_type: DealType): void {
		this.deal_type = deal_type;
	}

	public getAuctionType(): string | undefined {
		return this.auction_type;
	}

	public setAuctionType(auction_type: string): void {
		this.auction_type = auction_type;
	}

	public setBillingIds(billingIds: id[] | undefined) {
		this.billing_ids = billingIds;
	}

	public getBillingIds(): id[] | undefined {
		return this.billing_ids;
	}

	public getStrategy(): StrategyModel | undefined {
		return this.strategy;
	}

	public setStrategy(strategy: StrategyModel | undefined): void {
		this.strategy = strategy;
	}

	public getDestinationUrl(): url | undefined {
		const primary = this.getPrimaryCampaignDestination();
		return primary?.getDestinationUrl() || this.dest_url;
	}

	public setDestinationUrl(dest_url: url): void {
		this.dest_url = dest_url;
	}

	public getCampaignDestinations(): CampaignDestinationLinkModel[] {
		return this.campaign_destinations || [];
	}

	public getPrimaryCampaignDestination(): CampaignDestinationLinkModel | undefined {
		const links = this.getCampaignDestinations();
		return links.find(link => link.isActive()) || links[0];
	}

	public getCampaignDestinationByCatalogId(catalogId?: id): CampaignDestinationLinkModel | undefined {
		if (catalogId == null) {
			return undefined;
		}
		return this.getCampaignDestinations().find(link => String(link.getCatalogDestinationId()) === String(catalogId));
	}

	public getAvailableDestinations(destinations: DestinationModel[]): DestinationModel[] {
		const linkedIds = new Set(this.getCampaignDestinations().map(link => String(link.getCatalogDestinationId())));
		return destinations.filter(destination => {
			const destinationId = destination.getId();
			return destinationId != null && !linkedIds.has(String(destinationId));
		});
	}

	public addCampaignDestination(
		destination: DestinationModel,
		accounts?: { getId(): id | undefined; getUseInternalPricing(): boolean | undefined }[]
	): CampaignDestinationLinkModel {
		if (!this.campaign_destinations) {
			this.campaign_destinations = [];
		}
		const link = new CampaignDestinationLinkModel({
			status: Statuses.Active,
			weight: 1,
		});
		link.applyFromDestination(destination, this.getAdvertiser(), accounts);
		this.campaign_destinations.push(link);
		this.syncPrimaryDestinationUrl();
		return link;
	}

	public removeCampaignDestination(catalogDestinationId: id): void {
		this.campaign_destinations = this.getCampaignDestinations().filter(
			link => String(link.getCatalogDestinationId()) !== String(catalogDestinationId)
		);
		this.syncPrimaryDestinationUrl();
	}

	public clearDestinationSelection(): void {
		this.campaign_destinations = [];
		this.campaign_destination = undefined;
		this.dest_url = '';
	}

	public getCampaignDestinationsFormData(): ICampaignDestinationForm[] {
		return this.getCampaignDestinations()
			.map(link => link.getFormData())
			.filter((form): form is ICampaignDestinationForm => Boolean(form));
	}

	public syncPrimaryDestinationUrl(): void {
		this.dest_url = this.getPrimaryCampaignDestination()?.getDestinationUrl() || '';
	}

	/** @deprecated Use getPrimaryCampaignDestination / getCampaignDestinations */
	public getSelectedDestinationId(): id | undefined {
		return this.getPrimaryCampaignDestination()?.getSelectedDestinationId();
	}

	/** @deprecated Use getPrimaryCampaignDestination */
	public getSelectedWebappsCid(): id | string | undefined {
		return this.getPrimaryCampaignDestination()?.getSelectedWebappsCid();
	}

	/** @deprecated Use getPrimaryCampaignDestination */
	public getDestinationUseInternalPricing(): boolean {
		return this.getPrimaryCampaignDestination()?.getDestinationUseInternalPricing() === true;
	}

	public getProductId(): string {
		return this.yid || '';
	}

	public setProductId(productId: string): void {
		this.yid = productId.trim();
	}

	public getTargeting(): TargetingModel | undefined {
		return this.targeting;
	}

	public setTargeting(targeting: TargetingModel): void {
		this.targeting = targeting;
	}

	public getBlockedHours(): number[] | undefined {
		return this.blocked_hours;
	}

	public setBlockedHours(blockedHours: number[]): void {
		this.blocked_hours = blockedHours;
	}

	public getDailyBudget(): DailyBudgetModel | undefined {
		return this.daily_budget;
	}

	public setDailyBudget(dailyBudget: DailyBudgetModel): void {
		this.daily_budget = dailyBudget;
	}

	public getDefaultPayout(): number | undefined {
		return this.default_payout;
	}

	public setDefaultPayout(defaultPayout: number | undefined): void {
		this.default_payout = defaultPayout;
	}

	public getAllowMultipleSales(): boolean | undefined {
		return this.allow_multiple_sales;
	}

	public setAllowMultipleSales(allow_multiple_sales: boolean): void {
		this.allow_multiple_sales = allow_multiple_sales;
	}

	public getPhases(): CampaignPhaseModel[] {
		return this.campaign_phases || [];
	}

	public clearPhases() {
		this.campaign_phases = [];
	}

	public getPhaseById(id?: id): CampaignPhaseModel | undefined {
		return this.campaign_phases?.find(p => p.phase.id === id);
	}

	public getAvailablePhases(phases: IEntity[]) {
		const currentPhasesIds = new Set(this.campaign_phases?.map(p => p.phase.id));

		return phases.filter(p => !currentPhasesIds?.has(p.id));
	}

	public setPhases(phases: CampaignPhaseModel[]): void {
		this.campaign_phases = phases;
	}

	public addPhase(phase: IEntity): void {
		this.campaign_phases?.push(new CampaignPhaseModel({ phase, cache_key_generator: DEFAULT_CACHE_KEY_GENERATOR }));
	}

	public removePhase(id: id): void {
		this.campaign_phases = this.campaign_phases?.filter(p => p.phase.id !== id);
	}

	public setPhase(phaseData: Partial<ICampaignPhase> | undefined): void {
		if (!phaseData || !phaseData.phase) {
			return;
		}
		const index = this.campaign_phases?.findIndex(p => p.phase.id === phaseData.phase?.id);

		if (index === undefined || index < 0 || !this.campaign_phases) {
			return;
		}

		const current = this.campaign_phases[index];
		this.campaign_phases[index] = new CampaignPhaseModel({
			...current,
			...phaseData,
			strategy: phaseData.strategy ?? current.strategy,
			daily_budget: phaseData.daily_budget ?? current.daily_budget,
			rules: phaseData.rules ?? current.rules,
			cache_key_generator: phaseData.cache_key_generator ?? current.getCacheKeyGenerator(),
		});
	}

	public getRules(phaseId?: id): RulesModel | undefined {
		return this.campaign_phases?.find(p => p.phase.id === phaseId)?.rules;
	}

	public setRules(phaseId: number, rules: RulesModel): void {
		const phase = this.campaign_phases?.find(p => p.phase.id === phaseId);

		if (phase) {
			phase.setRules(rules);
		}
	}

	public getBlackListedSites(): Set<string> | undefined {
		return this.black_listed_sites;
	}
	public getWhiteListedSites(): Set<string> | undefined {
		return this.white_listed_sites;
	}

	public setBlackListedSites(black_listed_sites: Set<string>): void {
		this.black_listed_sites = black_listed_sites;
	}
	public setWhiteListedSites(white_listed_sites: Set<string>): void {
		this.white_listed_sites = white_listed_sites;
	}

	public getBlacklistedKeys(): Set<string> | undefined {
		return this.black_listed_keys;
	}
	public getWhitelistedKeys(): Set<string> | undefined {
		return this.white_listed_keys;
	}

	public getCreatives(): CreativeModel[] | undefined {
		return this.creatives;
	}
	public setCreatives(creative: CreativeModel[]): void {
		this.creatives = creative;
	}
	public getCreativeSelectionMode(): CreativeSelectionMode | undefined {
		return this.creative_selection_mode;
	}
	public setCreativeSelectionMode(creativeSelectionMode: CreativeSelectionMode): void {
		this.creative_selection_mode = creativeSelectionMode;
	}
	public getDeals(): DealModel[] | undefined {
		return this.deals;
	}
	public setDeals(deals: DealModel[]): void {
		this.deals = deals;
	}
	public setBlacklistedSizes(black_listed_sizes: BannerSize[]): void {
		this.black_listed_sizes = black_listed_sizes;
	}
	public setWhitelistedSizes(white_listed_sizes: BannerSize[]): void {
		this.white_listed_sizes = white_listed_sizes;
	}
	public getBlacklistedSizes(): BannerSize[] | undefined {
		return this.black_listed_sizes;
	}
	public getWhitelistedSizes(): BannerSize[] | undefined {
		return this.white_listed_sizes;
	}
	public getBannerListType(): WhiteBlackLists | undefined {
		return this.banner_list_type;
	}
	public setBannerListType(banner_list_type: WhiteBlackLists): void {
		this.banner_list_type = banner_list_type;
	}
	public getDailyDomainMaxLoss(): number | undefined | null {
		return this.daily_domain_max_loss;
	}
	public setDailyDomainMaxLoss(daily_domain_max_loss: number | null): void {
		this.daily_domain_max_loss = daily_domain_max_loss;
	}
}
